/**
 * The browser-side `window.app` facade. Stamped onto `globalThis.app` by
 * `src/pwa/main.tsx` BEFORE the React tree mounts so any shared module that
 * calls `window.app.X(...)` resolves into this object instead of the
 * Electron preload.
 *
 * Every method matches the shape exposed by `src/preload/index.ts`. Methods
 * that have no browser-meaningful behaviour (hotkeys, fake cursor, native
 * screenshot, OS permissions, window dragging) are stubbed — they either
 * no-op (event subscriptions return a no-op unsubscriber) or throw
 * `NotSupportedOnPwa` if a caller actually invokes them. The mobile shell
 * never calls those, so the stubs exist only to satisfy types and to keep
 * any future shared-module import safe.
 */
import * as claude from './claude';
import * as oai from './openai-compatible';
import { GROQ_CONFIG, NVIDIA_CONFIG } from './openai-compatible';
import { speech, testDeepgramKey } from './speech';
import { runCode } from './code-runner';
import { keys } from './keystore';
import type {
  ArchiveEntry,
  ClaudeStartArgs,
  ExportSessionPayload,
  ExportSessionResult,
  HotkeyEvent,
  LlmProviderId,
  PermInfo,
  PermKey,
  PermStatus,
  DesktopSource,
  RestoreHistoryMessage,
  SessionPayload,
  TestVisionResult,
} from '../../shared/ipc';

class NotSupportedOnPwa extends Error {
  constructor(what: string) {
    super(`${what} is not supported in the mobile PWA build.`);
    this.name = 'NotSupportedOnPwa';
  }
}

const noopUnsubscribe = (): void => {
  /* nothing to clean up — we never registered anything */
};

export const browserApi = {
  // ----- Anthropic key + models -----------------------------------------
  async saveApiKey(key: string): Promise<void> {
    keys.setAnthropic(key);
  },
  async hasApiKey(): Promise<boolean> {
    return Boolean(keys.getAnthropic());
  },
  async clearApiKey(): Promise<void> {
    keys.clearAnthropic();
  },
  /** Provider-aware test. The desktop preload accepts an optional second
   *  `providerId` arg; we mirror that signature here. Defaults to
   *  Anthropic when no providerId is given (matches desktop default). */
  async testApiKey(key: string, providerId?: LlmProviderId): Promise<void> {
    if (providerId === 'nvidia') {
      await oai.testKey(NVIDIA_CONFIG, key);
      return;
    }
    if (providerId === 'groq') {
      await oai.testKey(GROQ_CONFIG, key);
      return;
    }
    await claude.testKey(key);
  },
  /** Provider-aware list. PWA's Anthropic path queries the SDK; the
   *  OAI-compat providers don't have a cheap browser-side list (the
   *  desktop hits `/v1/models` from main, but doing that here would
   *  add another proxy round-trip per Settings open). For now PWA
   *  returns the empty list for non-Anthropic — Settings falls back to
   *  the curated `getFallbackModelsFor(provider)` from the shared store. */
  async listModels(providerId?: LlmProviderId): Promise<{ id: string; label: string }[]> {
    if (!providerId || providerId === 'anthropic') return claude.listModels();
    return [];
  },

  // ----- Deepgram key ---------------------------------------------------
  async saveDeepgramKey(key: string): Promise<void> {
    keys.setDeepgram(key);
  },
  async hasDeepgramKey(): Promise<boolean> {
    return Boolean(keys.getDeepgram());
  },
  async clearDeepgramKey(): Promise<void> {
    keys.clearDeepgram();
  },
  async testDeepgramKey(key: string): Promise<void> {
    await testDeepgramKey(key);
  },

  // ----- NVIDIA NIM key (LLM + ASR) -------------------------------------
  // Stored locally (same risk profile as Deepgram on PWA) and sent to
  // NVIDIA's OpenAI-compatible endpoint at request time. The PWA shell
  // doesn't yet wire NIM as an LLM provider — these are stubs so the
  // type surface matches the desktop preload.
  async saveNvidiaKey(key: string): Promise<void> {
    keys.setNvidia(key);
  },
  async hasNvidiaKey(): Promise<boolean> {
    return Boolean(keys.getNvidia());
  },
  async clearNvidiaKey(): Promise<void> {
    keys.clearNvidia();
  },
  async testNvidiaKey(key: string): Promise<void> {
    await oai.testKey(NVIDIA_CONFIG, key);
  },

  // ----- Groq key (LLM) ------------------------------------------------
  async saveGroqKey(key: string): Promise<void> {
    keys.setGroq(key);
  },
  async hasGroqKey(): Promise<boolean> {
    return Boolean(keys.getGroq());
  },
  async clearGroqKey(): Promise<void> {
    keys.clearGroq();
  },
  async testGroqKey(key: string): Promise<void> {
    await oai.testKey(GROQ_CONFIG, key);
  },
  async testVisionModel(
    _providerId: LlmProviderId,
    _modelId: string,
  ): Promise<TestVisionResult> {
    return { ok: false, error: 'Not supported in PWA' };
  },

  // ----- Session persistence (desktop-only) ----------------------------
  // PWA already has its own IndexedDB-backed flows; cross-restart
  // session restore is out of scope for the mobile build. These are
  // no-op stubs that keep the shared renderer compiling.
  async loadSession(): Promise<SessionPayload | null> {
    return null;
  },
  async saveSession(_payload: SessionPayload): Promise<void> {
    /* no-op in PWA */
  },
  async clearSession(): Promise<void> {
    /* no-op in PWA */
  },

  // ----- Sessions archive (desktop-only) -------------------------------
  // PWA archive surface is empty for now — list returns []; load returns
  // null; save throws (matches NotSupportedOnPwa pattern); delete/rename
  // are no-ops; restore-history is a no-op.
  async archiveList(): Promise<ArchiveEntry[]> {
    return [];
  },
  async archiveLoad(_id: string): Promise<SessionPayload | null> {
    return null;
  },
  async archiveSave(_args: { title?: string; session: SessionPayload }): Promise<ArchiveEntry> {
    throw new NotSupportedOnPwa('Sessions archive save');
  },
  async archiveDelete(_id: string): Promise<void> {
    /* no-op in PWA */
  },
  async archiveRename(_args: { id: string; title: string }): Promise<void> {
    /* no-op in PWA */
  },
  async claudeRestoreHistory(_args: {
    conversationId: string;
    history: RestoreHistoryMessage[];
  }): Promise<void> {
    /* no-op in PWA */
  },

  // ----- AssemblyAI key (STT) -------------------------------------------
  // Settings UI lets the user save the key now; the runtime mic-stream
  // routing through AssemblyAI's Universal-Streaming v3 WebSocket is
  // still deferred (the PWA's `speech` module is Deepgram-only). The
  // test probe below verifies the key against AssemblyAI's REST API,
  // which supports browser CORS for `Authorization: <key>` calls.
  async saveAssemblyAIKey(key: string): Promise<void> {
    keys.setAssemblyAI(key);
  },
  async hasAssemblyAIKey(): Promise<boolean> {
    return Boolean(keys.getAssemblyAI());
  },
  async clearAssemblyAIKey(): Promise<void> {
    keys.clearAssemblyAI();
  },
  /** Cheapest valid-key probe: a HEAD on the transcript list endpoint.
   *  200 → key works (returns the user's transcripts). 401/403 → bad
   *  key / wrong scope. Anything else → surface the body for diagnosis.
   *  Doesn't burn any AssemblyAI quota since we're not transcribing. */
  async testAssemblyAIKey(key: string): Promise<void> {
    const token = key.trim() || keys.getAssemblyAI() || '';
    if (!token) {
      throw new Error('No AssemblyAI API key set. Paste one or save it in Settings first.');
    }
    let res: Response;
    try {
      res = await fetch('https://api.assemblyai.com/v2/transcript?limit=1', {
        method: 'GET',
        headers: { Authorization: token },
      });
    } catch (err) {
      throw new Error(
        `AssemblyAI network error: ${(err as Error).message}. ` +
          'If the browser console shows a CORS block, the key may need to be tested via the desktop app.',
      );
    }
    if (res.ok) return;
    const body = await res.text().catch(() => '');
    if (res.status === 401 || res.status === 403) {
      throw new Error(
        `AssemblyAI rejected the API key (${res.status}). ` +
          'Get a fresh key at assemblyai.com → Account → API keys and paste it again.',
      );
    }
    if (res.status === 429) {
      throw new Error('AssemblyAI rate-limited the request. Wait a moment and try again.');
    }
    throw new Error(`AssemblyAI ${res.status}: ${body || res.statusText}`);
  },

  // ----- Session export -------------------------------------------------
  //
  // PWA can't pop a native save dialog or use Electron's printToPDF, so we
  // render in-browser:
  //  - PDF:    opens a new window with the export HTML and triggers the
  //            browser's native print dialog ("Save as PDF" option).
  //            No JS library — html2pdf.js was tried and consistently
  //            produced blank pages in the PWA / Vite + Workbox context.
  //            Native print is reliable on iOS Safari + Android Chrome.
  //  - MD-folder: JSZip bundles session.md + images/ inside a single .zip,
  //            mirroring the desktop's MD-folder layout. Also lazy-loaded.
  //
  // Both paths trigger a download via a synthesized <a download> click —
  // works on iOS Safari + Android Chrome + desktop browsers without any
  // permission dialog.
  async exportSession(payload: ExportSessionPayload): Promise<ExportSessionResult> {
    if (payload.format === 'pdf') {
      // Native print → Save as PDF. We tried html2pdf.js / html2canvas
      // first but it consistently produced blank pages in the PWA
      // (likely ESM-interop or canvas tainting in the Vite + Workbox
      // context). The print-dialog path is reliable everywhere —
      // iOS Safari, Android Chrome, desktop browsers — and gives users
      // the system's full PDF settings (margins, paper size, etc).
      //
      // We open a new tab, write the export HTML into it, attach
      // listeners that call window.print() once images have loaded,
      // then close after the dialog. If popup blockers intervene we
      // surface a clear error so the user can re-tap.
      const win = window.open('', '_blank');
      if (!win) {
        throw new Error(
          'Could not open print window — your browser blocked the popup. ' +
            'Allow popups for this site and tap Export again.',
        );
      }
      // Inject a tiny auto-print script at the bottom of the body so
      // the new window prints itself once images finish loading. We
      // also close the window after print so it doesn't linger.
      const autoPrint = `
        <script>
          (function () {
            function ready() {
              var imgs = Array.prototype.slice.call(document.images);
              return Promise.all(imgs.map(function (img) {
                if (img.complete) return Promise.resolve();
                return new Promise(function (res) {
                  img.addEventListener('load', res, { once: true });
                  img.addEventListener('error', res, { once: true });
                });
              }));
            }
            ready().then(function () {
              window.focus();
              window.print();
              setTimeout(function () { window.close(); }, 500);
            });
          })();
        </script>`;
      const htmlWithPrint = payload.html.replace('</body>', `${autoPrint}</body>`);
      win.document.open();
      win.document.write(htmlWithPrint);
      win.document.close();
      return { ok: true, path: payload.suggestedFilename };
    }

    // 'md-folder' → ZIP with session.md + images/<filename>.jpg
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    zip.file(`${payload.suggestedFolderName}/session.md`, payload.markdown);
    if (payload.images && payload.images.length > 0) {
      const imagesDir = zip.folder(`${payload.suggestedFolderName}/images`);
      if (imagesDir) {
        for (const img of payload.images) {
          imagesDir.file(img.filename, img.bytes);
        }
      }
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${payload.suggestedFolderName}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true, path: a.download };
  },

  // ----- Speech (mic only on PWA) --------------------------------------
  speech,

  // ----- LLM streaming (multi-provider) --------------------------------
  // `args.provider` selects the dispatch path. Anthropic uses the SDK
  // (`claude.ts`), NVIDIA + Groq use the browser OAI-compat fetch streamer
  // (`openai-compatible.ts`). Both publish events into the same listener
  // set via `claude.emitClaudeEvent`, so the renderer subscribes once.
  startClaude(args: ClaudeStartArgs): void {
    const provider = args.provider ?? 'anthropic';
    if (provider === 'anthropic') {
      void claude.start(args);
      return;
    }
    const cfg = provider === 'nvidia' ? NVIDIA_CONFIG : GROQ_CONFIG;
    void oai.startStream(cfg, args, {
      onDelta: (text) =>
        claude.emitClaudeEvent({
          type: 'delta',
          conversationId: args.conversationId,
          exchangeId: args.exchangeId,
          text,
        }),
      onDone: (fullText, cancelled) =>
        claude.emitClaudeEvent({
          type: 'done',
          conversationId: args.conversationId,
          exchangeId: args.exchangeId,
          fullText,
          cancelled,
        }),
      onError: (message) =>
        claude.emitClaudeEvent({
          type: 'error',
          conversationId: args.conversationId,
          exchangeId: args.exchangeId,
          message,
        }),
    });
  },
  cancelClaude(payload?: { conversationId: string; exchangeId?: string }): void {
    // Cancel routes through both — the conversationId could belong to
    // either path. Cheap to call both since the inner Maps gate on
    // existence.
    claude.cancel(payload);
    oai.cancelStream(payload);
  },
  onClaudeEvent: claude.onClaudeEvent,

  // ----- Code execution ------------------------------------------------
  async runCode(
    code: string,
    language?: 'javascript' | 'typescript',
  ): Promise<{ stdout: string; stderr: string; error?: string; durationMs: number }> {
    return runCode(code, language ?? 'javascript');
  },

  // ----- Stubs: desktop-only features the PWA shell never calls --------
  async getDesktopSources(): Promise<DesktopSource[]> {
    return [];
  },
  async testCapture(): Promise<{ ok: boolean; source: string; preview: string; error?: string }> {
    return { ok: false, source: 'pwa', preview: '', error: 'Not supported in PWA' };
  },
  async triggerScreenshot(): Promise<{
    jpegBytes: ArrayBuffer;
    takenAt: number;
    width: number;
    height: number;
  }> {
    throw new NotSupportedOnPwa('Native screenshot');
  },
  permissions: {
    async list(): Promise<PermInfo[]> {
      return [];
    },
    async request(_key: PermKey): Promise<{ status: PermStatus; opened: boolean }> {
      return { status: 'unknown', opened: false };
    },
  },
  relaunch(): void {
    window.location.reload();
  },
  /** No-op on the PWA — the browser doesn't expose `BrowserWindow.setOpacity`.
   *  Returns 1 (fully opaque) so any UI that renders an opacity readout
   *  shows "100 %" and shared modules don't crash. */
  async getOpacity(): Promise<number> {
    return 1;
  },
  onOpacityChange(_handler: (opacity: number) => void): () => void {
    return noopUnsubscribe;
  },
  /** No-op stubs — region screenshot is a desktop hotkey path that
   *  doesn't exist in the mobile build. The shared Overlay subscribes
   *  to these on mount; PWA returns a no-op unsubscribe. */
  onScreenshotCaptured(
    _handler: (payload: {
      jpegBytes: ArrayBuffer;
      takenAt: number;
      width: number;
      height: number;
    }) => void,
  ): () => void {
    return noopUnsubscribe;
  },
  onScreenshotError(_handler: (message: string) => void): () => void {
    return noopUnsubscribe;
  },
  /** No-op stub — desktop-only feature. Mobile browsers can't resize their
   *  own window. Shared modules can call this safely. */
  setCompact(_compact: boolean): void {
    /* intentionally empty */
  },
  /** No-op stub — layout is fixed in the PWA. Shared code calls this on
   *  layout-change effects; we ignore. */
  applyLayout(_layout: 'classic' | 'horizontal'): void {
    /* intentionally empty */
  },
  /** No-op stub — minimize-to-icon is desktop-only (mobile browsers
   *  can't resize their own window). */
  setMinimized(_minimized: boolean): void {
    /* intentionally empty */
  },
  onMinimizedChange(_handler: (minimized: boolean) => void): () => void {
    return noopUnsubscribe;
  },
  windowDragStart(): void {
    /* noop — no frameless window in PWA */
  },
  windowDragEnd(): void {
    /* noop */
  },
  quit(): void {
    /* noop — browser tab can't quit itself */
  },
  onHotkey(_handler: (key: HotkeyEvent) => void): () => void {
    return noopUnsubscribe;
  },
  onCursorPos(_handler: (pos: { x: number; y: number } | null) => void): () => void {
    return noopUnsubscribe;
  },
  onClipboardSend(
    _handler: (payload: {
      text: string;
      fromSelection: boolean;
      target?: 'chat' | 'code';
      error?: string;
    }) => void,
  ): () => void {
    return noopUnsubscribe;
  },
};

// Type assert to the global Window['app'] shape. Done with `as unknown` to
// dodge a tiny shape-mismatch on the speech sub-object's `_marker` properties
// that some bundles emit.
export type BrowserApi = typeof browserApi;
